import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type ReactNode } from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cn } from '@eon/utils';

/**
 * Switch is a styled wrapper over Radix Switch. Use SwitchField when you
 * want a label-and-description row.
 */
export const Switch = forwardRef<
  ElementRef<typeof SwitchPrimitive.Root>,
  ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>
>(({ className, ...props }, ref) => (
  <SwitchPrimitive.Root
    ref={ref}
    className={cn(
      'peer focus-visible:ring-ring inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
      'disabled:cursor-not-allowed disabled:opacity-50',
      'data-[state=checked]:bg-primary data-[state=unchecked]:bg-input',
      className,
    )}
    {...props}
  >
    <SwitchPrimitive.Thumb
      className={cn(
        'bg-background pointer-events-none block h-4 w-4 rounded-full shadow-lg ring-0 transition-transform',
        'data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0',
      )}
    />
  </SwitchPrimitive.Root>
));
Switch.displayName = 'Switch';

export interface SwitchFieldProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  className?: string;
}

export const SwitchField = forwardRef<HTMLLabelElement, SwitchFieldProps>(
  ({ checked, onCheckedChange, label, description, disabled, className }, ref) => (
    <label
      ref={ref}
      className={cn(
        'bg-muted/30 hover:bg-muted/50 flex cursor-pointer items-center justify-between gap-4 rounded-md p-3',
        disabled && 'pointer-events-none opacity-50',
        className,
      )}
    >
      <span className="flex flex-col">
        <span className="text-sm font-medium leading-none">{label}</span>
        {description ? <span className="text-muted-foreground mt-1 text-xs">{description}</span> : null}
      </span>
      <Switch checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
    </label>
  ),
);
SwitchField.displayName = 'SwitchField';
